import type {ReactElement} from 'react';
import React, {useEffect, useState} from 'react';
import {DigitalSignageSubPanel} from '@Panels/poster-publishing-options-panel/components/publishing-digital-signage-list/components/digital-signage-sub-panel/digital-signage-sub-panel';
import {Text, TextSize} from '@Components/text';
import type {PosterProductDataForProcessDesign} from '@Panels/poster-publishing-options-panel/poster-publishing-options-panel.types';
import {usePosterPageHashedId} from '@Panels/poster-publishing-options-panel/poster-publishing-options-panel.hooks';
import {buildIframeEmbedSnippet, useCopyButton} from '@Panels/poster-publishing-options-panel/components/publishing-digital-signage-list/publishing-digital-signage-list.hooks';
import type {PublishingYodeckPanelProps} from './publishing-yodeck-panel.types';
import {DigitalSignageOption} from '@Panels/poster-publishing-options-panel/components/publishing-digital-signage-list/publishing-digital-signage-list.types';
import {setErrorMessageForPublishing, setErrorOnPublishPanel} from '@Panels/poster-publishing-options-panel/poster-publishing-options-slice';
import {useAppDispatch} from '@/hooks';
import {useProductUsed} from '@Panels/poster-publishing-options-panel/poster-publishing-options-panel-library';

export function PublishingYodeckPanel({...props}: PublishingYodeckPanelProps): ReactElement<any> {
  const [generatedIframe, setGeneratedIframe] = useState('');
  const [clicked, setClicked] = useState(false);
  const productUsed = useProductUsed();
  const dispatch = useAppDispatch();
  const pageHashedId = usePosterPageHashedId();
  const pageOrderId = productUsed?.existingPageOrders?.[pageHashedId] ?? productUsed?.orderId;

  useEffect(() => {
    try {
      if (pageOrderId) {
        void window.PMW.readLocal('v/generateDisplayCode', {
          orderId: pageOrderId,
        })
          .then((response: string) => {
            const generatedIframe = buildIframeEmbedSnippet(response, productUsed ?? ({} as PosterProductDataForProcessDesign));
            setGeneratedIframe(generatedIframe);
          })
          .catch(() => {
            dispatch(setErrorOnPublishPanel(true));
            dispatch(setErrorMessageForPublishing(window.i18next.t('pmwjs_error_message')));
          });
      }
    } catch (e) {
      console.error(e);

      dispatch(setErrorOnPublishPanel(true));
      dispatch(setErrorMessageForPublishing(window.i18next.t('pmwjs_error_message')));
    }
  }, [pageOrderId]);

  useEffect(() => {
    setTimeout(() => {
      setClicked(false);
    }, 3000);
  }, [clicked]);

  const listItemElement: ReactElement<any> = (
    <div className="content-sub-text">
      <Text val={window.i18next.t('pmwjs_yodeck_follow_steps')} size={TextSize.XSMALL} />
      <div className="flexbox flex-v-row color-supplementary-text">
        <Text dangerouslySetInnerHTML className="spacing-p-b-2" val={window.i18next.t('pmwjs_yodeck_step1')} size={TextSize.XSMALL} />
        <Text className="spacing-p-b-2" val={window.i18next.t('pmwjs_yodeck_step2')} size={TextSize.XSMALL} />
        <Text val={window.i18next.t('pmwjs_yodeck_step3')} size={TextSize.XSMALL} />
      </div>
    </div>
  );

  const onCopyLink = (): void => {
    void window.PMW.writeLocal('cart/addOrderUsedByDigitalSignage', {
      orderId: pageOrderId,
      type: DigitalSignageOption.YODECK,
    });
  };

  return (
    <DigitalSignageSubPanel
      panelHeading={window.i18next.t('pmwjs_yodeck')}
      heading={window.i18next.t('pmwjs_yodeck_digital_signage')}
      showButton
      showInputBar
      webpageLink={generatedIframe}
      inputBarIcon="icon-code"
      listItem={listItemElement}
      onClose={props.onClose}
      onClick={() => {
        window.PMW.copyTextToClipboard(generatedIframe);
        onCopyLink();
      }}
      buttonComponent={useCopyButton(clicked, generatedIframe, setClicked, onCopyLink)}
      imgSrc="publishing-yodeck.png"
    />
  );
}
